/**
 * SaaS AI tool definitions and executor.
 *
 * Provides Gemini-compatible function declarations and a browser-side
 * executor that operates on Supabase data instead of the filesystem.
 * Mirrors the dev mode tool-calling pattern from packages/ai/src/tools/.
 */

import type {SupabaseClient} from '@supabase/supabase-js';
import type {AudioClipConfig} from '@cascade/core';
import type {Scene} from './db';
import * as db from './db';

// ---------------------------------------------------------------------------
// Code validation helpers
// ---------------------------------------------------------------------------

/**
 * Ensure the scene code has a default export.
 * Gemini sometimes generates `export const Foo = () => ...` instead of
 * `export default function Foo() { ... }`. This auto-fixes that.
 */
function ensureDefaultExport(code: string): string {
  // Already has a default export -- nothing to do
  if (/export\s+default\s/.test(code)) return code;

  // Pattern: export const Foo = (...) => { ... }
  // or:      export const Foo = (...) => (...)
  // or:      export function Foo(...) { ... }
  const namedExportMatch = code.match(
    /export\s+(const|function)\s+(\w+)/,
  );
  if (namedExportMatch) {
    const componentName = namedExportMatch[2];
    // Replace the first `export const` or `export function` with just `const`/`function`
    // and add `export default` referencing the component at the end
    let fixed = code.replace(
      /export\s+(const|function)\s+/,
      '$1 ',
    );
    fixed += `\nexport default ${componentName};\n`;
    return fixed;
  }

  return code;
}

// ---------------------------------------------------------------------------
// Tool context (passed to executor)
// ---------------------------------------------------------------------------

export interface ToolContext {
  supabase: SupabaseClient;
  projectId: string;
  scenes: Scene[];
  width: number;
  height: number;
  fps: number;
  audioAnalyses: Record<string, unknown>;
  audioClips: AudioClipConfig[];
  /** Refresh scenes list after mutation. */
  onScenesChanged: () => void;
}

export interface ToolResult {
  content: string;
  isError: boolean;
}

// ---------------------------------------------------------------------------
// Tool declarations (Gemini functionDeclarations format)
// ---------------------------------------------------------------------------

export const SAAS_TOOL_DECLARATIONS = [
  {
    name: 'list_scenes',
    description:
      'List all scenes in the project with their names, order index, and duration in frames.',
    parameters: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'read_scene',
    description:
      'Read the full source code of a scene with line numbers. Use list_scenes first to see available scene names.',
    parameters: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description:
            'Scene name (e.g. "Scene 1"). Case-insensitive partial match.',
        },
      },
      required: ['name'],
    },
  },
  {
    name: 'edit_scene',
    description:
      'Edit a scene by replacing an exact string match in its code. old_string must match the scene code character-for-character (including whitespace and newlines). Only the first occurrence is replaced. Use read_scene first to see the exact content.',
    parameters: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description: 'Scene name to edit.',
        },
        old_string: {
          type: 'string',
          description:
            'The exact string to find and replace. Must match character-for-character.',
        },
        new_string: {
          type: 'string',
          description: 'The replacement string.',
        },
      },
      required: ['name', 'old_string', 'new_string'],
    },
  },
  {
    name: 'rewrite_scene',
    description:
      'Completely replace a scene\'s code with new code. Use this when you need to reimagine or fully rewrite an existing scene. For small changes, use edit_scene instead. The code must be a complete Remotion component that default-exports a function.',
    parameters: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description: 'Name of the existing scene to rewrite.',
        },
        code: {
          type: 'string',
          description: 'Complete new TSX source code for the scene.',
        },
      },
      required: ['name', 'code'],
    },
  },
  {
    name: 'create_scene',
    description:
      'Create a brand new scene. Only use this to add a NEW scene to the project -- to modify an existing scene use edit_scene or rewrite_scene. The code must be a complete Remotion component that default-exports a function. Always start with /** @jsxImportSource react */.',
    parameters: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description: 'Name for the new scene.',
        },
        code: {
          type: 'string',
          description: 'Complete TSX source code for the scene.',
        },
      },
      required: ['name', 'code'],
    },
  },
  {
    name: 'get_audio_analysis',
    description:
      'Get audio analysis data for all audio clips in the project. Returns word-level timings (start/end in seconds), sentence boundaries, and pause detection. Use this to sync animations to voiceover or music.',
    parameters: {
      type: 'object',
      properties: {},
    },
  },
  {
    name: 'get_project_info',
    description:
      'Get project metadata including canvas dimensions, fps, total scene count, and a list of audio clips with their offsets.',
    parameters: {
      type: 'object',
      properties: {},
    },
  },
];

// ---------------------------------------------------------------------------
// Scene lookup helper
// ---------------------------------------------------------------------------

function findScene(scenes: Scene[], name: string): Scene | undefined {
  const lower = name.toLowerCase();
  // Exact match first
  const exact = scenes.find(s => s.name.toLowerCase() === lower);
  if (exact) return exact;
  // Partial match
  return scenes.find(s => s.name.toLowerCase().includes(lower));
}

function addLineNumbers(code: string): string {
  return code
    .split('\n')
    .map((line, i) => `${String(i + 1).padStart(4)} | ${line}`)
    .join('\n');
}

// ---------------------------------------------------------------------------
// Tool executor
// ---------------------------------------------------------------------------

export async function executeSaaSTool(
  toolName: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Promise<ToolResult> {
  try {
    switch (toolName) {
      case 'list_scenes':
        return executeListScenes(ctx);

      case 'read_scene':
        return executeReadScene(args.name as string, ctx);

      case 'edit_scene':
        return await executeEditScene(
          args.name as string,
          args.old_string as string,
          args.new_string as string,
          ctx,
        );

      case 'rewrite_scene':
        return await executeRewriteScene(
          args.name as string,
          args.code as string,
          ctx,
        );

      case 'create_scene':
        return await executeCreateScene(
          args.name as string,
          args.code as string,
          ctx,
        );

      case 'get_audio_analysis':
        return executeGetAudioAnalysis(ctx);

      case 'get_project_info':
        return executeGetProjectInfo(ctx);

      default:
        return {content: `Unknown tool: ${toolName}`, isError: true};
    }
  } catch (err) {
    return {
      content: `Tool error: ${err instanceof Error ? err.message : String(err)}`,
      isError: true,
    };
  }
}

// ---------------------------------------------------------------------------
// Individual tool implementations
// ---------------------------------------------------------------------------

function executeListScenes(ctx: ToolContext): ToolResult {
  if (ctx.scenes.length === 0) {
    return {content: 'No scenes in the project yet.', isError: false};
  }

  const lines = ctx.scenes.map(
    (s, i) =>
      `${i + 1}. "${s.name}" (${s.duration_frames} frames, ~${(s.duration_frames / ctx.fps).toFixed(1)}s)`,
  );

  return {
    content: `Project has ${ctx.scenes.length} scene(s):\n${lines.join('\n')}`,
    isError: false,
  };
}

function executeReadScene(name: string, ctx: ToolContext): ToolResult {
  const scene = findScene(ctx.scenes, name);
  if (!scene) {
    const available = ctx.scenes.map(s => `"${s.name}"`).join(', ');
    return {
      content: `Scene "${name}" not found. Available scenes: ${available || 'none'}`,
      isError: true,
    };
  }

  const numbered = addLineNumbers(scene.code);
  return {
    content: `Scene "${scene.name}" (${scene.code.length} chars, ${scene.duration_frames} frames):\n\n${numbered}`,
    isError: false,
  };
}

async function executeEditScene(
  name: string,
  oldString: string,
  newString: string,
  ctx: ToolContext,
): Promise<ToolResult> {
  const scene = findScene(ctx.scenes, name);
  if (!scene) {
    const available = ctx.scenes.map(s => `"${s.name}"`).join(', ');
    return {
      content: `Scene "${name}" not found. Available scenes: ${available || 'none'}`,
      isError: true,
    };
  }

  if (!scene.code.includes(oldString)) {
    return {
      content: `old_string not found in "${scene.name}". The string must match character-for-character. Use read_scene to see the exact content.`,
      isError: true,
    };
  }

  // Replace first occurrence only
  const newCode = scene.code.replace(oldString, newString);

  await db.updateScene(ctx.supabase, scene.id, {code: newCode});

  // Update the in-memory scene so subsequent reads see the change
  scene.code = newCode;

  ctx.onScenesChanged();

  return {
    content: `Edited "${scene.name}": replaced ${oldString.length} chars with ${newString.length} chars.`,
    isError: false,
  };
}

async function executeRewriteScene(
  name: string,
  code: string,
  ctx: ToolContext,
): Promise<ToolResult> {
  const scene = findScene(ctx.scenes, name);
  if (!scene) {
    const available = ctx.scenes.map(s => `"${s.name}"`).join(', ');
    return {
      content: `Scene "${name}" not found. Available scenes: ${available || 'none'}`,
      isError: true,
    };
  }

  // Ensure JSX pragma
  let finalCode = code;
  if (!finalCode.includes('@jsxImportSource')) {
    finalCode = '/** @jsxImportSource react */\n' + finalCode;
  }
  // Ensure default export (Gemini sometimes uses named exports)
  finalCode = ensureDefaultExport(finalCode);

  // Calculate scene duration from audio if available, otherwise keep existing
  const audioDuration = getTotalAudioDuration(ctx);
  const durationFrames = audioDuration > 0
    ? Math.ceil(audioDuration * ctx.fps)
    : scene.duration_frames;

  await db.updateScene(ctx.supabase, scene.id, {code: finalCode, duration_frames: durationFrames});

  // Update in-memory scene
  scene.code = finalCode;
  scene.duration_frames = durationFrames;

  ctx.onScenesChanged();

  return {
    content: `Rewrote "${scene.name}" (${finalCode.length} chars, ${durationFrames} frames / ${(durationFrames / ctx.fps).toFixed(1)}s). The viewport will update.`,
    isError: false,
  };
}

async function executeCreateScene(
  name: string,
  code: string,
  ctx: ToolContext,
): Promise<ToolResult> {
  // Ensure JSX pragma
  let finalCode = code;
  if (!finalCode.includes('@jsxImportSource')) {
    finalCode = '/** @jsxImportSource react */\n' + finalCode;
  }
  // Ensure default export (Gemini sometimes uses named exports)
  finalCode = ensureDefaultExport(finalCode);

  // Calculate scene duration from audio if available
  const audioDuration = getTotalAudioDuration(ctx);
  const durationFrames = audioDuration > 0
    ? Math.ceil(audioDuration * ctx.fps)
    : ctx.fps * 5; // Default 5 seconds

  const newScene = await db.createScene(ctx.supabase, {
    project_id: ctx.projectId,
    name,
    code: finalCode,
    order_index: ctx.scenes.length,
    duration_frames: durationFrames,
  });

  // Add to in-memory scenes list
  ctx.scenes.push(newScene);

  ctx.onScenesChanged();

  return {
    content: `Created scene "${name}" (${finalCode.length} chars). It's now visible in the scene list.`,
    isError: false,
  };
}

/**
 * Get the longest audio clip duration in seconds.
 * Used to auto-set scene duration to cover the full audio.
 */
function getTotalAudioDuration(ctx: ToolContext): number {
  let maxDuration = 0;
  for (const key of Object.keys(ctx.audioAnalyses)) {
    const analysis = ctx.audioAnalyses[key] as Record<string, unknown>;
    const meta = analysis?.meta as Record<string, unknown> | undefined;
    if (meta?.duration) {
      maxDuration = Math.max(maxDuration, Number(meta.duration));
    }
  }
  return maxDuration;
}

function executeGetAudioAnalysis(ctx: ToolContext): ToolResult {
  const analyses = ctx.audioAnalyses;
  const keys = Object.keys(analyses);
  const fps = ctx.fps;

  if (keys.length === 0) {
    return {
      content:
        'No audio analysis data available. Audio clips need to be analyzed first (right-click an audio clip > "Analyze audio").',
      isError: false,
    };
  }

  // Match analysis keys to audio clips by source URL for better identification
  const clipsBySource = new Map<string, AudioClipConfig>();
  for (const clip of ctx.audioClips) {
    clipsBySource.set(clip.src, clip);
  }

  // Build a structured, readable output that highlights the transcript
  const parts: string[] = [];
  parts.push(`Audio analysis for ${keys.length} clip(s) (project fps: ${fps}):\n`);

  for (let clipIdx = 0; clipIdx < keys.length; clipIdx++) {
    const key = keys[clipIdx];
    const analysis = analyses[key] as Record<string, unknown>;
    const transcript = analysis?.transcript as Record<string, unknown> | undefined;
    const meta = analysis?.meta as Record<string, unknown> | undefined;
    const pauses = analysis?.pauses as unknown[] | undefined;

    // Extract a readable filename from the source URL key
    const clip = clipsBySource.get(key);
    const filename = key.split('/').pop()?.split('?')[0] ?? key;
    const offsetFrames = clip?.offset ?? 0;

    parts.push(`\n=== AUDIO CLIP ${clipIdx + 1}: "${filename}" ===`);
    parts.push(`Source key: ${key}`);
    parts.push(`Timeline offset: frame ${offsetFrames} (${(offsetFrames / fps).toFixed(2)}s)`);
    if (meta?.duration) {
      parts.push(`Duration: ${meta.duration}s (${Math.round((meta.duration as number) * fps)} frames)`);
    }

    if (transcript?.text) {
      parts.push(`\nTRANSCRIPT (these are the EXACT words spoken -- use them in your animation):`);
      parts.push(`"${transcript.text}"\n`);

      const words = transcript.words as Array<{word: string; start: number; end: number}> | undefined;
      if (words) {
        parts.push(`WORD TIMINGS (${words.length} words, fps=${fps}) -- use these exact timestamps:`);
        for (const w of words) {
          parts.push(`  "${w.word}" start=${w.start}s end=${w.end}s (frame ${Math.round(w.start * fps)}-${Math.round(w.end * fps)})`);
        }
      }

      const sentences = transcript.sentences as Array<{text: string; start: number; end: number}> | undefined;
      if (sentences) {
        parts.push(`\nSENTENCES (${sentences.length}):`);
        for (const s of sentences) {
          parts.push(`  "${s.text}" start=${s.start}s end=${s.end}s`);
        }
      }
    }

    if (pauses && pauses.length > 0) {
      parts.push(`\nPAUSES (${pauses.length}):`);
      for (const p of pauses as Array<{start: number; end: number; duration: number; type: string}>) {
        parts.push(`  ${p.type} pause at ${p.start}s-${p.end}s (${p.duration}ms)`);
      }
    }
  }

  if (keys.length > 1) {
    parts.push(`\nNOTE: There are ${keys.length} audio clips. Each clip has its own independent transcript.`);
    parts.push(`Focus on the clip that is most relevant to the user's request. If unclear, use the clip with the most words/longest duration.`);
    parts.push(`Do NOT combine text from different clips into one scene unless the user explicitly asks.`);
  }

  parts.push(`\nIMPORTANT: Use the EXACT words and timestamps above. Do NOT substitute your own text. FPS is ${fps}.`);

  let output = parts.join('\n');
  // Truncate if extremely large
  if (output.length > 8000) {
    output = output.slice(0, 8000) + '\n... (truncated)';
  }

  return {content: output, isError: false};
}

function executeGetProjectInfo(ctx: ToolContext): ToolResult {
  const info = {
    canvas: {width: ctx.width, height: ctx.height},
    fps: ctx.fps,
    sceneCount: ctx.scenes.length,
    scenes: ctx.scenes.map(s => ({name: s.name, durationFrames: s.duration_frames})),
    audioClips: ctx.audioClips.map(c => ({
      id: c.id,
      src: c.src,
      offsetFrames: c.offset,
      trackId: c.trackId,
    })),
    hasAudioAnalysis: Object.keys(ctx.audioAnalyses).length > 0,
  };

  return {
    content: JSON.stringify(info, null, 2),
    isError: false,
  };
}
